<app-detail-panel [isOpen]="true" [resizable]="true" [(widthPx)]="widthPx" (panelClose)="onClose()">
  <!-- Header — title row + optional manager-action row. Title is an
       inline-editable input (Notion/Linear-style) so renaming the list
       doesn't need a separate field in the body. Visual contract: looks
       like a heading until focused; reveals an input ring on focus.
       Save commits through the persistent footer like every other field. -->
  <div class="panel-header">
    <div class="panel-title-row">
      <app-section-icon class="panel-title-icon" name="vendors" />
      <app-editable-panel-title
        [value]="name()"
        (valueChange)="name.set($event)"
        [readonly]="!canMutate() || !!list().archived_at"
        placeholder="Untitled list"
        ariaLabel="List name"
      />
      @if (list().archived_at) {
        <app-status-indicator
          appearance="inline"
          status="inactive"
          [label]="'vendor_lists.archived_badge' | transloco"
        />
      }
    </div>

    <div class="panel-header-actions">
      @if (canEdit() && !list().archived_at) {
        <span [matTooltip]="writesBlockedReason()" matTooltipPosition="below">
          <app-button
            type="danger"
            size="small"
            variant="outline"
            [disabled]="busy() || writesBlocked()"
            (clicked)="requestArchive()"
          >
            Archive
          </app-button>
        </span>
      }
      <!-- Close button — same `.panel-icon-btn` chrome the other
           drawers (control / framework / document / task) use, so the
           dismiss affordance lands in the same spot on every drawer
           shell. Routes through `onClose()` so the dirty-state confirm
           guards accidental discards. -->
      <button type="button" class="panel-icon-btn" (click)="onClose()" aria-label="Close panel">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  @if (error()) {
    <div class="modal-error">
      <span>{{ error() }}</span>
      <button type="button" (click)="dismissError()" aria-label="Dismiss">×</button>
    </div>
  }

  <!-- Tabs — Details / Vendors / History. Mirrors the inventory-detail
       page tabbed shape so the edit shells across the vendor feature read
       as one pattern. Tab content swaps inline; the persistent-form footer
       at the bottom of the drawer commits whichever tab was edited. -->
  <app-panel-tabs
    [tabs]="tabs()"
    [activeTab]="activeTab()"
    (tabChange)="onTabChange($any($event))"
  />

  <div class="panel-body">
    @if (activeTab() === 'details') {
      <!-- Details — `.form-pane` + `.detail-card` is the canonical
           shape used by the dossier Details tab and the per-relationship
           Overview tab (see `routes/vendors/.../details-tab.html`,
           `overview-tab.html`). Using it here makes the drawer read as
           one product with those surfaces. -->
      <div class="form-pane">
        <!-- Editable: description.
             The list name lives in the header (inline-editable) so it
             isn't duplicated here. Description is the one free-text
             input on this tab; the card title acts as its label, so
             the per-field `<label>` is hidden visually but kept for
             screen readers via `.visually-hidden`. -->
        <article class="detail-card">
          <header class="card-header">
            <div class="card-title-block">
              <h3 class="card-title">Description</h3>
              <p class="card-hint">
                Optional context — who this list serves, what it covers, when it was last reviewed.
                Surfaces on hover in the list grid.
              </p>
            </div>
          </header>
          <div class="field">
            <label for="vl-desc" class="visually-hidden">Description</label>
            <textarea
              id="vl-desc"
              rows="5"
              placeholder="Describe this list…"
              [ngModel]="description()"
              (ngModelChange)="description.set($event)"
              [readOnly]="!canMutate() || !!list().archived_at"
            ></textarea>
          </div>
        </article>

        <!-- Read-only: lifecycle summary.
             Same vertical dt/dd pattern the side-rails of the
             inventory-detail + registry-detail pages use, so metadata
             across the vendor feature reads as one design language.
             Full provenance lives on the History tab — this is the
             at-a-glance summary. -->
        <article class="detail-card">
          <header class="card-header">
            <div class="card-title-block">
              <h3 class="card-title">Activity</h3>
              <p class="card-hint">
                Lifecycle stamps. The full edit history lives on the History tab.
              </p>
            </div>
          </header>
          <dl class="meta-list">
            <div class="meta-row">
              <dt>Vendors</dt>
              <dd>{{ selectedMembers().size }}</dd>
            </div>
            <div class="meta-row">
              <dt>Created</dt>
              <dd>{{ list().created_at | date: 'mediumDate' }}</dd>
            </div>
            <div class="meta-row">
              <dt>Last updated</dt>
              <dd>{{ list().updated_at | date: 'mediumDate' }}</dd>
            </div>
            @if (list().archived_at) {
              <div class="meta-row">
                <dt>Archived</dt>
                <dd>{{ list().archived_at | date: 'mediumDate' }}</dd>
              </div>
            }
          </dl>
        </article>
      </div>
    } @else if (activeTab() === 'vendors') {
      <div class="form-pane">
        <article class="detail-card">
          <header class="card-header">
            <div class="card-title-block">
              <h3 class="card-title">Members</h3>
              <p class="card-hint">
                {{ 'vendor_list_modal.vendors_hint' | transloco }}
              </p>
            </div>
          </header>
          @if (membersLoading()) {
            <p class="muted small">Loading…</p>
          } @else {
            <!-- Picker doesn't expose a `readonly` input; the whole
                 subtree goes `inert` (natively non-interactive + skipped
                 by tab focus) when the user lacks authority or writes
                 are blocked by sub/feature. Same guard the sibling
                 title/description inputs use, so member add/remove
                 stays in sync with the rest of the drawer. -->
            <div [attr.inert]="!canMutate() || !!list().archived_at ? '' : null">
              <app-vendor-member-picker
                [vendors]="inventoryVendors()"
                [registry]="registry()"
                [(selection)]="selectedMembers"
              />
            </div>
          }
        </article>
      </div>
    } @else if (activeTab() === 'history') {
      <div class="form-pane">
        <article class="detail-card">
          <header class="card-header">
            <div class="card-title-block">
              <h3 class="card-title">History</h3>
              <p class="card-hint">
                Every change to this list — creation, edits, archival, member changes — is recorded
                by the audit service.
              </p>
            </div>
          </header>
          <app-audit-timeline
            [events]="auditEvents()"
            [loading]="historyLoading()"
            entityLabel="this list"
          />
        </article>
      </div>
    }
  </div>

  <!-- Persistent-form footer — only renders when dirty. Mirrors the
       dossier Details tab + the inventory-detail per-tab forms so the
       save model is identical across the app. -->
  @if (isDirty()) {
    <footer class="panel-footer">
      <app-button type="secondary" size="medium" [disabled]="busy()" (clicked)="onRevert()">
        Revert
      </app-button>
      <span [matTooltip]="writesBlockedReason()" matTooltipPosition="above">
        <app-button
          type="primary"
          size="medium"
          [disabled]="!canSave() || writesBlocked()"
          [loading]="busy()"
          (clicked)="onSave()"
        >
          Save changes
        </app-button>
      </span>
    </footer>
  }
</app-detail-panel>

<!-- Archive confirm — hosted by the drawer rather than the parent route
     so the user is asked from inside the surface they were editing, and
     the drawer can keep the BE error in its own error state. -->
<app-confirm-modal
  [isVisible]="showArchiveConfirm()"
  title="Archive list"
  [message]="
    'Archive the list &quot;' +
    (name() || list().name) +
    '&quot;? It will no longer appear in the main view.'
  "
  confirmText="Archive"
  cancelText="Cancel"
  [isLoading]="busy()"
  (confirmed)="confirmArchive()"
  (cancelled)="cancelArchive()"
  (modalClosed)="cancelArchive()"
/>

<!-- Discard-unsaved-changes confirm — fires when the user attempts to
     close the drawer while it's dirty. Industry-standard guard; uses
     the same `<app-confirm-modal>` primitive as every other "are you
     sure?" prompt in the app. -->
<app-confirm-modal
  [isVisible]="showDiscardConfirm()"
  title="Discard unsaved changes?"
  message="You have unsaved edits. Close anyway? Your changes will be lost."
  confirmText="Discard"
  cancelText="Keep editing"
  (confirmed)="confirmDiscard()"
  (cancelled)="cancelDiscard()"
  (modalClosed)="cancelDiscard()"
/>
